import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  MessageCircle,
  Send,
  X,
  Minimize2,
  ChevronDown,
  Paperclip,
  Camera,
  Image,
  FileText,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Max file size 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const ChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState([]);
  const [connected, setConnected] = useState(true);
  const [usePolling, setUsePolling] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const wsAttemptCountRef = useRef(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Get session token
  const getSessionToken = useCallback(() => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token') {
        return value;
      }
    }
    return null;
  }, []);

  // Connect WebSocket (global chat - no company_id)
  const connectWebSocket = useCallback(() => {
    if (!user) return;

    const token = getSessionToken();
    if (!token) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(`${WS_URL}/ws/chat?token=${token}`);

    ws.onopen = () => {
      setConnected(true);
      console.log('Chat WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'new_message') {
        setMessages(prev => [...prev, data.message]);
        
        // Increment unread if chat is closed and message is from someone else
        if (!isOpen && data.message.sender_id !== user.user_id) {
          setUnreadCount(prev => prev + 1);
        }
      } else if (data.type === 'typing') {
        setTypingUsers(prev => {
          if (!prev.find(u => u.user_id === data.user_id)) {
            return [...prev, { user_id: data.user_id, user_name: data.user_name }];
          }
          return prev;
        });
        
        // Remove typing indicator after 2 seconds
        setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u.user_id !== data.user_id));
        }, 2000);
      } else if (data.type === 'notification') {
        // Handle real-time notification
        if (!isOpen) {
          setUnreadCount(prev => prev + 1);
        }
      }
    };

    ws.onclose = () => {
      setConnected(false);
      console.log('Chat WebSocket disconnected');
      
      wsAttemptCountRef.current += 1;
      
      // After 3 failed attempts, fall back to polling
      if (wsAttemptCountRef.current >= 3) {
        console.log('WebSocket connection failed, falling back to polling');
        setUsePolling(true);
        setConnected(true); // Mark as "connected" via polling
        return;
      }
      
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    // Timeout to detect connection failure (10 seconds)
    const connectionTimeout = setTimeout(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket connection timeout');
        wsRef.current.close();
      }
    }, 10000);

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      setConnected(true);
      setUsePolling(false); // Upgrade to WebSocket mode
      wsAttemptCountRef.current = 0;
      console.log('Chat WebSocket connected - real-time mode enabled');
    };

    wsRef.current = ws;
  }, [user, getSessionToken, isOpen]);

  // Fetch initial messages (global chat - no company filter)
  const fetchMessages = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await fetch(`${API_URL}/api/chat/messages?limit=50`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [user]);

  // Fetch users for @mentions
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/users`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  // Initialize
  useEffect(() => {
    if (user) {
      fetchMessages();
      fetchUsers();
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [user, fetchMessages, fetchUsers, connectWebSocket]);

  // Polling fallback - fetch messages periodically when WebSocket fails
  useEffect(() => {
    if (usePolling && isOpen && user) {
      // Poll every 3 seconds when using polling mode
      pollingIntervalRef.current = setInterval(fetchMessages, 3000);
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [usePolling, isOpen, user, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isMinimized]);

  // Clear unread when opening chat
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    setShowAttachMenu(false);
    
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" is too large. Maximum size is 5MB.`);
        continue;
      }
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        setAttachments(prev => [...prev, {
          filename: file.name,
          content_type: file.type,
          size: file.size,
          url: base64,
          preview: file.type.startsWith('image/') ? base64 : null
        }]);
      };
      reader.readAsDataURL(file);
    }
    
    // Clear the input
    e.target.value = '';
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    const content = newMessage.trim();
    setSending(true);

    // Always use REST API for messages with attachments
    try {
      const response = await fetch(`${API_URL}/api/chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: content,
          attachments: attachments.length > 0 ? attachments.map(a => ({
            filename: a.filename,
            content_type: a.content_type,
            size: a.size,
            url: a.url
          })) : null
        })
      });

      if (response.ok) {
        setNewMessage('');
        setAttachments([]);
        setShowMentions(false);
        await fetchMessages();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Send typing indicator (only via WebSocket)
    if (!usePolling && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
      typingTimeoutRef.current = setTimeout(() => {}, 1000);
    }

    // Check for @mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      const hasSpaceAfter = textAfterAt.includes(' ') && textAfterAt.split(' ').length > 2;
      
      if (!hasSpaceAfter) {
        setMentionFilter(textAfterAt.toLowerCase());
        setShowMentions(true);
        setMentionIndex(0);
        return;
      }
    }
    setShowMentions(false);
  };

  const handleKeyDown = (e) => {
    if (showMentions) {
      const filteredUsers = users.filter(u => 
        u.name.toLowerCase().includes(mentionFilter) && u.user_id !== user?.user_id
      );
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => Math.min(prev + 1, filteredUsers.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredUsers.length > 0) {
        e.preventDefault();
        insertMention(filteredUsers[mentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const insertMention = (mentionUser) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const beforeAt = newMessage.slice(0, lastAtIndex);
    const newValue = `${beforeAt}@${mentionUser.name} `;
    setNewMessage(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const formatMessageContent = (content) => {
    if (!content) return null;
    const parts = content.split(/(@\w+(?:\s+\w+)*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span key={i} className="bg-blue-100 text-blue-700 px-0.5 rounded text-xs font-medium">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const renderAttachment = (attachment, isOwn) => {
    const isImage = attachment.content_type?.startsWith('image/');
    
    if (isImage && attachment.url) {
      return (
        <div className="mt-2">
          <img 
            src={attachment.url} 
            alt={attachment.filename}
            className="max-w-full max-h-40 rounded-lg cursor-pointer hover:opacity-90"
            onClick={() => window.open(attachment.url, '_blank')}
          />
          <p className="text-[10px] mt-1 opacity-70">{attachment.filename}</p>
        </div>
      );
    }
    
    return (
      <div className={`mt-2 flex items-center gap-2 p-2 rounded-lg ${isOwn ? 'bg-slate-800' : 'bg-slate-200'}`}>
        <FileText className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs truncate">{attachment.filename}</p>
          <p className="text-[10px] opacity-70">{formatFileSize(attachment.size)}</p>
        </div>
        {attachment.url && (
          <a 
            href={attachment.url} 
            download={attachment.filename}
            className="p-1 hover:bg-white/10 rounded"
          >
            <Download className="w-3 h-3" />
          </a>
        )}
      </div>
    );
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      production: 'bg-slate-100 text-slate-600',
      lab: 'bg-amber-100 text-amber-700',
      accounts: 'bg-blue-100 text-blue-700',
      manager: 'bg-purple-100 text-purple-700'
    };
    return classes[role] || classes.production;
  };

  const filteredMentionUsers = users.filter(u => 
    u.name.toLowerCase().includes(mentionFilter) && u.user_id !== user?.user_id
  );

  if (!user) return null;

  return (
    <>
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
      />
      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*"
        capture="environment"
      />

      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all z-50 hover:scale-105"
          data-testid="chat-widget-btn"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 transition-all duration-200 ${
            isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
          }`}
          data-testid="chat-widget"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 h-14 border-b border-slate-200 bg-slate-900 rounded-t-xl">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-white" />
              <div>
                <h3 className="font-semibold text-white text-sm">Team Chat</h3>
                <p className="text-xs text-slate-400 truncate max-w-[150px]">
                  All team members {usePolling && '(sync mode)'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Connection indicator */}
              <div 
                className={`w-2 h-2 rounded-full mr-2 ${connected ? 'bg-green-400' : 'bg-red-400'}`} 
                title={connected ? (usePolling ? 'Connected (polling)' : 'Connected (real-time)') : 'Disconnected'}
              />
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-slate-800 rounded transition-colors"
                data-testid="chat-minimize-btn"
              >
                {isMinimized ? <ChevronDown className="w-4 h-4 text-white" /> : <Minimize2 className="w-4 h-4 text-white" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-slate-800 rounded transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Chat Body */}
          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 h-[370px]">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <MessageCircle className="w-10 h-10 mb-2" />
                    <p className="text-sm">No messages yet</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex gap-2 ${msg.sender_id === user?.user_id ? 'flex-row-reverse' : ''}`}
                      data-testid={`chat-msg-${msg.message_id}`}
                    >
                      {/* Avatar */}
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-slate-600">
                        {msg.sender_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      
                      {/* Message */}
                      <div className={`max-w-[75%] ${msg.sender_id === user?.user_id ? 'items-end' : 'items-start'}`}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="font-medium text-xs text-slate-700">{msg.sender_name}</span>
                          <span className={`text-[10px] px-1 py-0.5 rounded capitalize ${getRoleBadgeClass(msg.sender_role)}`}>
                            {msg.sender_role}
                          </span>
                        </div>
                        <div className={`p-2 rounded-lg text-sm ${
                          msg.sender_id === user?.user_id 
                            ? 'bg-slate-900 text-white' 
                            : 'bg-slate-100 text-slate-900'
                        }`}>
                          {msg.content && (
                            <p className="whitespace-pre-wrap break-words text-xs">
                              {msg.sender_id === user?.user_id ? msg.content : formatMessageContent(msg.content)}
                            </p>
                          )}
                          {/* Render attachments */}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="space-y-1">
                              {msg.attachments.map((att, idx) => (
                                <div key={idx}>
                                  {renderAttachment(att, msg.sender_id === user?.user_id)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 mt-0.5 block">
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Typing indicator */}
              {typingUsers.length > 0 && (
                <div className="px-3 pb-1 text-xs text-slate-500">
                  {typingUsers.map(u => u.user_name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
              )}

              {/* Attachment Preview */}
              {attachments.length > 0 && (
                <div className="px-3 py-2 border-t border-slate-200 bg-slate-50">
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="relative group">
                        {att.preview ? (
                          <img src={att.preview} alt={att.filename} className="w-16 h-16 object-cover rounded-lg" />
                        ) : (
                          <div className="w-16 h-16 bg-slate-200 rounded-lg flex flex-col items-center justify-center">
                            <FileText className="w-5 h-5 text-slate-500" />
                            <span className="text-[8px] text-slate-500 mt-1 px-1 truncate max-w-full">{att.filename.slice(-8)}</span>
                          </div>
                        )}
                        <button
                          onClick={() => removeAttachment(idx)}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="border-t border-slate-200 p-3 relative">
                {/* Mention Suggestions */}
                {showMentions && filteredMentionUsers.length > 0 && (
                  <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                    {filteredMentionUsers.slice(0, 5).map((u, index) => (
                      <button
                        key={u.user_id}
                        onClick={() => insertMention(u)}
                        className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm ${
                          index === mentionIndex ? 'bg-slate-100' : ''
                        }`}
                      >
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-slate-900 text-xs">{u.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Attachment Menu */}
                {showAttachMenu && (
                  <div className="absolute bottom-full left-3 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    <button
                      onClick={() => { fileInputRef.current?.click(); }}
                      className="w-full px-4 py-2 flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm"
                      data-testid="attach-file-btn"
                    >
                      <Paperclip className="w-4 h-4 text-slate-500" />
                      <span>Attach File</span>
                    </button>
                    <button
                      onClick={() => { cameraInputRef.current?.click(); }}
                      className="w-full px-4 py-2 flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm"
                      data-testid="take-photo-btn"
                    >
                      <Camera className="w-4 h-4 text-slate-500" />
                      <span>Take Photo</span>
                    </button>
                    <button
                      onClick={() => { fileInputRef.current?.click(); }}
                      className="w-full px-4 py-2 flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm"
                      data-testid="upload-image-btn"
                    >
                      <Image className="w-4 h-4 text-slate-500" />
                      <span>Upload Image</span>
                    </button>
                  </div>
                )}

                <form onSubmit={sendMessage} className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                    className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-md transition-colors"
                    data-testid="chat-attach-btn"
                  >
                    <Paperclip className="w-4 h-4 text-slate-500" />
                  </button>
                  <Input
                    ref={inputRef}
                    value={newMessage}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Type @ to mention..."
                    className="flex-1 h-9 text-sm"
                    data-testid="chat-widget-input"
                    disabled={sending}
                  />
                  <Button
                    type="submit"
                    disabled={(!newMessage.trim() && attachments.length === 0) || sending}
                    className="h-9 px-3 bg-slate-900 hover:bg-slate-800"
                    data-testid="chat-widget-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
